import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "../api/errors.ts";
import type { components } from "../api/schema";
import type { Device } from "../devices/devices.ts";
import { formatDayMonth } from "../format.ts";
import { useApiClient } from "../session/SessionProvider.tsx";
import type { StatusBadgeTone } from "../ui/StatusBadge/StatusBadge.tsx";

export type Message = components["schemas"]["MessageResponse"];
export type FeedFilter = components["schemas"]["FeedFilter"];
type FeedPage = components["schemas"]["MessageListResponse"];

/** Ключ всех лент: события реального времени инвалидируют их разом. */
export const feedQueryKey = ["feed"] as const;

/** Лента со всех E-va пользователя: закреплённые первыми, затем новые. Скоупинг владелец/гость — на backend. */
export function useFeed(filter: FeedFilter, limit: number) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...feedQueryKey, { filter, limit }],
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/feed", { params: { query: { filter, limit } } });
      if (!data) throw ApiError.from(response, error);
      return data.messages;
    },
  });
}

const messageQueryPrefix = ["message"] as const;
const messageQueryKey = (messageId: string) => [...messageQueryPrefix, messageId] as const;

/** Одно сообщение (`GET /v1/messages/{id}`) — для шторки деталей, в том числе открытой по прямой ссылке. */
export function useMessage(messageId: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: messageQueryKey(messageId),
    enabled,
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/messages/{messageId}", {
        params: { path: { messageId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
  });
}

/** Закрепить или открепить (только владелец E-va). Порядок ленты меняется: она запрашивается заново. */
export function usePinMessage(messageId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pinned: boolean) => {
      const options = { params: { path: { messageId } } };
      const { data, error, response } = pinned
        ? await client.POST("/v1/messages/{messageId}/pin", options)
        : await client.DELETE("/v1/messages/{messageId}/pin", options);
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: (message) => messageChanged(queryClient, message),
  });
}

/**
 * Ключи идемпотентности нажатий «Показать снова», исход которых неизвестен (сеть, 5xx), — по id сообщения.
 * Живут вне карточки: повтор из истории после ошибки в ленте — тот же запрос для backend'а.
 */
const pendingReplayKeys = new Map<string, string>();

/**
 * «Показать снова» (только владелец E-va). Повтор нажатия с неизвестным исходом уходит с тем же
 * `Idempotency-Key`, и backend не поставит сообщение в очередь дважды. После успеха или отказа backend'а (4xx)
 * исход известен: следующее нажатие — новый показ с новым ключом.
 */
export function useReplayMessage(messageId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const idempotencyKey = pendingReplayKeys.get(messageId) ?? crypto.randomUUID();
      pendingReplayKeys.set(messageId, idempotencyKey);
      const { data, error, response } = await client.POST("/v1/messages/{messageId}/replay", {
        params: { path: { messageId }, header: { "Idempotency-Key": idempotencyKey } },
      });
      if (!data) throw ApiError.from(response, error);
      return data.message;
    },
    onSuccess: (message) => {
      pendingReplayKeys.delete(messageId);
      messageChanged(queryClient, message);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status < 500) pendingReplayKeys.delete(messageId);
    },
  });
}

/** Удалить (автор или владелец E-va): сообщение пропадает из всех лент. */
export function useDeleteMessage(messageId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await client.DELETE("/v1/messages/{messageId}", {
        params: { path: { messageId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: messageQueryKey(messageId) });
      void queryClient.invalidateQueries({ queryKey: feedQueryKey });
    },
  });
}

/**
 * Картинка не загрузилась. Если подписанная ссылка истекла, сообщение и ленты перезапрашиваются и приходят
 * со свежей; живая ссылка значит сбой хранилища, и перезапрос его не исправит. Возвращает, был ли перезапрос.
 */
export function refreshExpiredMedia(queryClient: QueryClient, message: Message, now = Date.now()): boolean {
  if (!message.mediaUrlExpiresAt || Date.parse(message.mediaUrlExpiresAt) > now) return false;
  // Ссылки картинок одной ленты истекают вместе: уже идущий перезапрос не перезапускается.
  void queryClient.invalidateQueries({ queryKey: messageQueryKey(message.id) }, { cancelRefetch: false });
  void queryClient.invalidateQueries({ queryKey: feedQueryKey }, { cancelRefetch: false });
  return true;
}

/** Перезагрузить ленты и открытые сообщения, например после смены имени: оно есть в карточках автора. */
export async function refetchMessages(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: feedQueryKey }),
    queryClient.invalidateQueries({ queryKey: messageQueryPrefix }),
  ]);
}

function messageChanged(queryClient: QueryClient, message: Message): void {
  queryClient.setQueryData(messageQueryKey(message.id), message);
  void queryClient.invalidateQueries({ queryKey: feedQueryKey });
}

export type MessageActions = { pin: boolean; replay: boolean; delete: boolean };

/** Как на backend: закрепление и повтор — только владелец E-va, удаление — ещё и автор. */
export function messageActions(message: Message, device: Device | undefined, viewerId: string | undefined): MessageActions {
  const owner = viewerId !== undefined && device?.ownerId === viewerId;
  return { pin: owner, replay: owner, delete: owner || message.sender.id === viewerId };
}

const HISTORY_PAGE_SIZE = 20;

/**
 * «Вся история»: та же лента постранично; `fetchNextPage` подгружает следующую страницу по `nextCursor`.
 * Курсор непрозрачный: его выдаёт и разбирает только backend.
 */
export function useFeedHistory(filter: FeedFilter) {
  const client = useApiClient();
  return useInfiniteQuery({
    queryKey: [...feedQueryKey, "history", { filter }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const query = { filter, limit: HISTORY_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) };
      const { data, error, response } = await client.GET("/v1/feed", { params: { query } });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    getNextPageParam: (page) => page.nextCursor,
    // Закрепление во время прокрутки может перенести сообщение на следующую страницу: показываем его один раз.
    select: (data) => uniqueById(data.pages.flatMap((page) => page.messages)),
  });
}

// Лента на главной хранит сообщения списком, «Вся история» — страницами.
type FeedCache = Message[] | InfiniteData<FeedPage, string | null>;

/**
 * Поправить сообщение в кэше без запроса к backend: в лентах и в шторке деталей. `update` возвращает новую версию,
 * `null` убирает сообщение из лент, а открытая шторка деталей перезапрашивает его и узнаёт, что сообщения больше нет.
 */
export function updateCachedMessage(
  queryClient: QueryClient,
  messageId: string,
  update: (message: Message) => Message | null,
): void {
  const apply = (messages: Message[]) =>
    messages.flatMap((message) => (message.id === messageId ? (update(message) ?? []) : [message]));
  queryClient.setQueriesData<FeedCache>({ queryKey: feedQueryKey }, (data) => {
    if (!data) return data;
    if (Array.isArray(data)) return apply(data);
    return { ...data, pages: data.pages.map((page) => ({ ...page, messages: apply(page.messages) })) };
  });
  const cached = queryClient.getQueryData<Message>(messageQueryKey(messageId));
  if (!cached) return;
  const next = update(cached);
  if (next) queryClient.setQueryData(messageQueryKey(messageId), next);
  else void queryClient.invalidateQueries({ queryKey: messageQueryKey(messageId) });
}

function uniqueById(messages: Message[]): Message[] {
  const seen = new Set<string>();
  return messages.filter((message) => !seen.has(message.id) && seen.add(message.id));
}

/** Как у backend (`MESSAGE_TEXT_MAX_LENGTH` в `app/api/v1/messages/schemas.py`) и прошивки: до 255 символов Unicode. */
export const MESSAGE_TEXT_MAX_LENGTH = 255;

/**
 * Длина текста так, как её считают backend и прошивка, — в code points.
 * `String.length` считает UTF-16: эмодзи вроде 😍 дали бы 2 вместо 1.
 */
export function textLength(text: string): number {
  let length = 0;
  for (const _ of text) length += 1;
  return length;
}

type MessageCreateRequest = components["schemas"]["MessageCreateRequest"];

/** Время показа на экране E-va — кнопки compose «30 сек / 2 мин / Вечно». */
export type DisplayTime = "30s" | "2m" | "forever";

type DisplayFields = Pick<MessageCreateRequest, "displayDurationSec" | "displayForever">;

// «Вечно» — только `displayForever`: вместе с `displayDurationSec` backend его отклоняет.
export const displayTimes: Record<DisplayTime, { label: string; fields: DisplayFields }> = {
  "30s": { label: "30 сек", fields: { displayDurationSec: 30, displayForever: false } },
  "2m": { label: "2 мин", fields: { displayDurationSec: 120, displayForever: false } },
  forever: { label: "Вечно", fields: { displayForever: true } },
};

/** Загруженное и обработанное вложение: фото (`image`) или GIF. */
export type MessageMedia = { assetId: string; kind: "image" | "gif" };

/** Без вложения `text` обязателен; с вложением это подпись, и пустая не отправляется. */
export type ImageFit = "contain" | "cover";
export type NewMessage = { deviceId: string; text: string; displayTime: DisplayTime; media?: MessageMedia; imageFit?: ImageFit; showSender?: boolean };

/**
 * Отправка из одного compose. `clientMessageId` и `Idempotency-Key` создаются один раз на compose и
 * уходят с каждым повтором: если ответ на прошлую попытку потерялся, backend вернёт уже созданное сообщение,
 * а не создаст второе. Новый compose — новые ключи.
 */
export function useSendMessage() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [keys] = useState(() => ({ clientMessageId: crypto.randomUUID(), idempotencyKey: crypto.randomUUID() }));
  return useMutation({
    mutationFn: async ({ deviceId, text, displayTime, media, imageFit = "contain", showSender = true }: NewMessage) => {
      const content: Pick<MessageCreateRequest, "type" | "text" | "mediaAssetId"> = media
        ? { type: media.kind, mediaAssetId: media.assetId, ...(text.trim() ? { text } : {}) }
        : { type: "text", text };
      const { data, error, response } = await client.POST("/v1/messages", {
        params: { header: { "Idempotency-Key": keys.idempotencyKey } },
        body: { deviceId, clientMessageId: keys.clientMessageId, ...content, ...displayTimes[displayTime].fields,
          imageFit: media ? imageFit : "contain",
          showSender,
        },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    // Новое сообщение появляется в лентах с тем статусом, который вернул backend.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedQueryKey }),
  });
}

export type DeliveryStatusView = { label: string; tone: StatusBadgeTone };

const queued: DeliveryStatusView = { label: "В очереди", tone: "neutral" };

const deliveryStatuses: Record<string, DeliveryStatusView> = {
  created: queued,
  queued,
  sent_to_device: { label: "Принято открыткой", tone: "primary" },
  shown: { label: "Показано", tone: "info" },
  seen: { label: "Просмотрено", tone: "success" },
  failed: { label: "Не доставлено", tone: "error" },
};

/** Подпись и тон плашки статуса доставки; неизвестный статус из будущей версии API считается очередью. */
export function deliveryStatus(status: string): DeliveryStatusView {
  return deliveryStatuses[status] ?? queued;
}

export function messageDeliveryStatus(message: Message): DeliveryStatusView {
  if (message.queueStatus === "pending") return queued;
  if (message.queueStatus === "sending") return { label: "Отправляется", tone: "primary" };
  if (message.queueStatus === "rendering") return { label: message.mediaAssetId ? "Загрузка фото" : "Ожидает показа", tone: "primary" };
  return deliveryStatus(message.deliveryStatus);
}

/** Имя автора в карточке: свои сообщения подписаны «Ты». */
export function authorName(message: Message, viewerId: string | undefined): string {
  return message.sender.id === viewerId ? "Ты" : senderName(message);
}

/** Имя автора как есть, без «Ты» — например, в деталях сообщения. */
export function senderName(message: Message): string {
  return message.sender.displayName?.trim() || "Без имени";
}

/**
 * Текст карточки: сам текст или подпись под картинкой. Медиа без подписи и без картинки (например, стикер)
 * подписано типом вложения, а картинке без подписи текст не нужен.
 */
export function cardText(message: Message): string | null {
  if (message.mediaUrl) return message.text;
  return message.text || messageTypeLabel(message);
}

/** Альтернативный текст картинки сообщения. */
export function mediaAlt(message: Message): string {
  return message.type === "gif" ? "GIF" : "Фото";
}

/** Тип в деталях сообщения: «Текст», «Фотография», «Текст + фотография», «GIF» (подпись у GIF не учитывается), «Стикер». */
export function messageTypeLabel(message: Message): string {
  switch (message.type) {
    case "text":
      return "Текст";
    case "gif":
      return "GIF";
    case "sticker":
      return "Стикер";
    default:
      return message.text ? "Текст + фотография" : "Фотография";
  }
}

const dateWithYearFormat = new Intl.DateTimeFormat("ru", { day: "numeric", month: "long", year: "numeric" });

/** Относительное время карточки: «5 мин назад», «3 ч назад», «2 дня назад», дальше — дата. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "вчера";
  if (days < 7) return `${days} ${days < 5 ? "дня" : "дней"} назад`;
  return date.getFullYear() === now.getFullYear() ? formatDayMonth(date) : dateWithYearFormat.format(date);
}

export type MessageGroup = { key: string; title: string; messages: Message[] };

/**
 * Группы «Всей истории» по локальной дате пользователя: «Сегодня, 15 июля 2026», «Вчера, …», «13 июля 2026».
 * Закреплённые backend отдаёт первыми — они собираются в группу «Закреплённые» перед датами,
 * кроме фильтра «Закреплённые», где закреплено всё и группы снова по датам.
 */
export function groupHistory(messages: Message[], filter: FeedFilter, now: Date = new Date()): MessageGroup[] {
  const groups: MessageGroup[] = [];
  for (const message of messages) {
    const pinnedFirst = message.pinned && filter !== "pinned";
    const date = new Date(message.createdAt);
    const key = pinnedFirst ? "pinned" : localDateKey(date);
    let group = groups.at(-1);
    if (group?.key !== key) {
      group = { key, title: pinnedFirst ? "Закреплённые" : dayTitle(date, now), messages: [] };
      groups.push(group);
    }
    group.messages.push(message);
  }
  return groups;
}

function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** Ru-формат с годом дописывает «г.», а в макете дата без него: собираем «15 июля 2026» по частям. */
const dayMonthFormat = new Intl.DateTimeFormat("ru", { day: "numeric", month: "long", timeZone: "UTC" });

/** Локальная дата пользователя без «г.»: «15 июля 2026». */
export function longDate(iso: string | Date): string {
  const date = new Date(iso);
  // Календарный день пользователя переносим в полночь UTC: форматтер не зависит от часового пояса,
  // зафиксированного при его создании, а дата остаётся локальной.
  const day = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return `${dayMonthFormat.format(day)} ${date.getFullYear()}`;
}

/** Локальное время пользователя: «10:32». */
export function clockTime(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function dayTitle(date: Date, now: Date): string {
  const day = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const label = longDate(date);
  const daysAgo = Math.round((today - day) / 86_400_000);
  if (daysAgo === 0) return `Сегодня, ${label}`;
  if (daysAgo === 1) return `Вчера, ${label}`;
  return label;
}
